import type { AllergensCopy } from '../types/allergens';

export const allergensRo: AllergensCopy = {
  seo: {
    title: 'Alergenii UE: cei 14 și legea, explicați simplu',
    description:
      'Cei 14 alergeni UE și ce cere legea de la restaurante, pe înțelesul tuturor. Află cum declari alergenii pe meniu, plus sursele ascunse care prind bucătăriile pe picior greșit.',
  },
  hero: {
    eyebrow: 'Ghid practic pentru proprietarii de restaurante',
    headingPre: 'Cei 14 alergeni UE, explicați pe',
    headingScribble: 'înțelesul tuturor',
    body: 'Află ce alergeni acoperă legea, unde se ascund și cum să le comunici clar clienților. Acest ghid te ghidează atât prin preparate, cât și prin reguli, fără limbaj juridic complicat.',
    factAllergenCount: '14 alergeni',
    factRegulation: 'Regulamentul (UE) nr. 1169/2011',
    factReadTime: '6 min de citit',
    imageAlt: 'O masă de restaurant cu un meniu SafestMenu cu cod QR',
  },
  intro: {
    leadPre: 'Dacă servești mâncare în UE, să numești acești 14 alergeni este deopotrivă',
    leadScribble1: 'o obligație legală',
    leadMid: 'și',
    leadScribble2: 'parte dintr-o ospitalitate bună',
    leadPost: '.',
    body: 'Acești alergeni se ascund în mai multe locuri decât recunosc majoritatea meniurilor. Această pagină explică cei 14 alergeni, legea din spatele lor și cum să îi declari clar - simplu și practic, ca să poți acționa chiar azi.',
    stats: [
      { big: '14', small: 'substanțe pe o listă fixă' },
      { big: 'Fiecare', small: 'restaurant, cafenea și bar din UE' },
      { big: 'Numiți', small: 'clar, niciodată lăsați la voia întâmplării' },
    ],
  },
  whyItMatters: {
    eyebrow: 'De ce contează',
    heading: 'Clienții în siguranță sunt cei care revin.',
    imageAlt: 'Clienți care se uită împreună pe un meniu, la o masă de restaurant',
    cards: [
      {
        title: 'O reacție poate fi gravă',
        text: 'Pentru unii clienți, o urmă ascunsă nu e o preferință - poate însemna un drum la spital.',
      },
      {
        title: 'Încrederea înseamnă ospitalitate',
        text: 'Faci lucrurile corect, iar oamenii se relaxează. Informațiile clare despre alergeni fac parte dintr-o primire călduroasă, nu din birocrație.',
      },
      {
        title: 'Așa spune legea',
        text: 'Restaurantul este operatorul responsabil din sectorul alimentar, așa că obligația îți revine ție, nu furnizorului tău.',
      },
    ],
  },
  theFourteen: {
    eyebrow: 'Lista de referință',
    heading: 'Fă cunoștință cu cei 14',
    intro: 'Aceștia sunt alergenii numiți în anexa II a Regulamentului (UE) nr. 1169/2011. Este o listă fixă și închisă - nu alegi tu pe care să îi urmărești, iar orice preparat care conține unul dintre ei trebuie să spună asta.',
    gotchas: {
      heading: '3 capcane care prind bucătăriile pe picior greșit',
      items: [
        'Uleiul de arahide contează mereu drept arahide, fie el rafinat sau nu. Doar uleiul de soia complet rafinat beneficiază de excepție, așa că nu extinde niciodată această scutire la uleiul de arahide, indiferent de denumire.',
        'Ovăzul este natural fără gluten, dar aproape întotdeauna contaminat încrucișat la măcinare. Tratează ovăzul simplu ca și cum ar conține gluten, dacă nu este certificat fără gluten.',
        'Lecitina de soia (E322) se regăsește într-o mare parte din ciocolată, produse tartinabile și produse de patiserie, așa că verifică fișele furnizorilor, nu doar ingredientele vizibile.',
      ],
    },
  },
  legislation: {
    eyebrow: 'Legea, pe scurt',
    heading: 'Regulamentul (UE) nr. 1169/2011',
    paragraphs: [
      'Regulile provin din Regulamentul (UE) nr. 1169/2011, numit adesea Regulamentul privind informarea consumatorilor cu privire la produsele alimentare, sau FIC. Fiind un regulament UE, se aplică direct în fiecare stat membru, inclusiv în România, fără să fie nevoie de o lege națională separată.',
      'Se aplică din 13 decembrie 2014. Articolul 9 listează informațiile pe care trebuie să le poarte produsele alimentare, articolul 21 stabilește cum sunt evidențiați alergenii pe produsele preambalate, iar anexa II este chiar lista celor 14 alergeni.',
      'Pentru mâncarea servită în vrac - preparatele pe care le gătești la comandă - articolul 44 face obligatorii informațiile despre alergeni, dar lasă la latitudinea fiecărei țări metoda de comunicare. Sancțiunile sunt stabilite tot la nivel național, prin legislația proprie de siguranță alimentară a fiecărei țări, nu prin regulament.',
    ],
    asksHeading: 'Ce ți se cere',
    asksItems: [
      'Numește cei 14 alergeni pe care trebuie să îi declari',
      'Se aplică atât meniurilor, cât și etichetelor',
      'Acoperă atât produsele preambalate, cât și mâncarea servită în vrac',
      'Trebuie să fie corectă și actualizată constant',
    ],
    natashasLaw:
      "Una despre care poate ai auzit, Natasha's Law din Regatul Unit, este o regulă britanică din 2021 - mai strictă decât standardul UE și care nu face deloc parte din legislația UE.",
  },
  steps: {
    eyebrow: 'Cum îi declari',
    heading: 'Transformă obligația într-o rutină simplă',
    intro: 'Acești 4 pași te duc de la un teanc de rețete la informații despre alergeni în care clientul poate avea încredere.',
    items: [
      {
        title: 'Construiește o singură sursă de adevăr',
        text: 'Marchezi care dintre cei 14 alergeni conține fiecare preparat, pe baza rețetelor și a fișelor furnizorilor. Ține totul într-un singur loc, ca bucătăria și sala să fie mereu de acord.',
      },
      {
        title: 'Pune informația acolo unde o văd clienții',
        text: 'Arat-o în scris, ca regulă generală: pe meniu, pe un tabel clar sau pe un meniu digital. Scrisul bate faptul de a te baza pe memoria cuiva.',
      },
      {
        title: 'Instruiește-ți echipa',
        text: 'Personalul din sală ar trebui să citească din fișă și să știe când să verifice cu bucătăria, în loc să ghicească. Răspunsuri corecte, de fiecare dată.',
      },
      {
        title: 'Menține informația la zi',
        text: 'Orice ajustare de rețetă sau furnizor nou poate adăuga sau elimina un alergen. Actualizezi fișa chiar în momentul în care ceva se schimbă.',
      },
    ],
    footnote: 'Reverifică de fiecare dată când o rețetă sau un furnizor se schimbă - de aici devin, pe tăcute, învechite majoritatea meniurilor.',
  },
  comparison: {
    eyebrow: 'Unde ajunge informația',
    heading: 'Preambalat versus în vrac',
    intro: 'Aceiași 14 alergeni, declarați în locuri diferite, în funcție de cum este vândută mâncarea. Aceasta este confuzia pe care o întâlnim cel mai des.',
    leftTitle: 'Produse preambalate',
    leftPoints: [
      'Vândute în ambalajul în care au fost puse înainte de a fi comandate, precum un sos îmbuteliat.',
      'Necesită o listă completă de ingrediente, cu cei 14 alergeni evidențiați în ea (articolul 21).',
      'Poartă și celelalte detalii de pe etichetă: denumire, cantitate și data durabilității minimale.',
    ],
    rightTitle: 'Mâncarea servită în vrac',
    rightPoints: [
      'Preparatele pe care le gătești și le servești la comandă, plus mâncarea ambalată pe loc, la cererea clientului.',
      'Nu necesită o listă tipărită de ingrediente, dar informațiile despre alergeni sunt obligatorii (articolul 44).',
      'Comunicate în modul permis de țara ta, dar mereu corecte și ușor de obținut înainte de comandă.',
    ],
  },
  crossContact: {
    eyebrow: 'În bucătărie',
    heading: 'Contaminarea încrucișată și „poate conține”',
    imageAlt: 'O suprafață de preparare din restaurant, curată și proaspăt ștearsă',
    bodyPre:
      'Declararea alergenilor care sunt ingrediente e un lucru. Contaminarea încrucișată e altul - un alergen care ajunge din greșeală prin friteuze, tocătoare sau cuțite comune. O',
    bodyScribble: 'urmă e suficientă',
    bodyPost:
      ', așa că „poate conține” este onest doar după ce ai analizat riscul real și nu îl poți elimina.',
    tiles: [
      'Separă zonele și ustensilele de preparare',
      'Curăță între comenzi',
      'Instruiește toată echipa',
      'Fii onest cu „poate conține”',
    ],
  },
  digital: {
    eyebrow: 'Unde ajută un meniu cu cod QR',
    heading: 'O singură editare, actuală la fiecare masă',
    body: 'Meniurile tipărite și tabelele laminate au un punct slab comun: menținerea lor la zi. Un meniu digital elimină asta. Marchezi fiecare preparat o singură dată, iar o editare ajunge instant la fiecare client - fără retipărire.',
    list: [
      'Marchezi cei 14 alergeni pentru fiecare preparat, o singură dată',
      'Clienții filtrează meniul după ce este sigur pentru ei',
      'Mereu versiunea actuală, în limba lor',
    ],
    footnote: 'Onest despre limite: un meniu cu cod QR nu îți face datele corecte și nu înlocuiește instruirea personalului. Rezolvă informația învechită, care este cea mai frecventă greșeală.',
    ctaLabel: 'Vezi cum gestionează SafestMenu alergenii',
    qrImageAlt: 'Un cartonaș cu cod QR SafestMenu lângă o cafea',
    dishImageAlt: 'Un preparat aranjat pe farfurie, afișat pe un meniu digital SafestMenu',
    journeyEyebrow: 'Traseul informației despre alergeni',
    journeyIntro: 'Informația despre alergeni este un flux cu 4 predări. Meniul tău - și un instrument precum SafestMenu - se află exact în punctul în care ajunge la client.',
  },
  faq: {
    eyebrow: 'Întrebări frecvente',
    heading: 'Răspunsuri rapide',
    items: [
      {
        q: 'Câți alergeni trebuie să declar?',
        a: 'Toți cei 14. Legislația UE stabilește o listă fixă în anexa II a Regulamentului 1169/2011, iar orice preparat care conține unul dintre ei trebuie să menționeze acest lucru. Poți semnala și alte lucruri despre care întreabă clienții, dar cei 14 reprezintă minimul legal.',
      },
      {
        q: 'Pot doar să le spun clienților să întrebe personalul?',
        a: 'Depinde de țara ta. Unele state membre permit comunicarea informațiilor despre alergeni la cerere, dacă un semn clar îi îndrumă pe clienți să întrebe, iar personalul oferă răspunsuri corecte. Altele, precum Franța, o impun în scris. Scrisul rămâne varianta cea mai sigură peste tot.',
      },
      {
        q: "Se aplică Natasha's Law restaurantului meu?",
        a: "Doar dacă te afli în Regatul Unit. Natasha's Law este o regulă britanică din 1 octombrie 2021, pentru mâncarea ambalată pe loc înainte ca un client să o comande, și este mai strictă decât standardul UE. Nu se aplică în România sau în restul UE.",
      },
      {
        q: 'Este uleiul de arahide scutit de la declarare?',
        a: 'Nu. Uleiul de arahide contează drept arahide, indiferent dacă este rafinat sau nu, așa că trebuie declarat mereu. Doar uleiul de soia complet rafinat beneficiază de această excepție, iar „groundnut oil” și „arachis oil” sunt doar alte denumiri pentru uleiul de arahide.',
      },
      {
        q: 'Cine răspunde dacă informația despre alergeni este greșită?',
        a: 'Tu. Restaurantul este operatorul din sectorul alimentar, așa că obligația de a oferi informații corecte îți revine ție, chiar dacă un furnizor a schimbat o rețetă fără să te anunțe. De aceea contează o singură fișă, mereu la zi.',
      },
      {
        q: 'Un meniu cu cod QR mă ține singur în conformitate?',
        a: 'Nu, și nu am pretinde asta. Un meniu digital menține informațiile despre alergeni la zi la toate mesele deodată, dar datele sunt la fel de bune ca ceea ce introduci tu. Ai nevoie în continuare de personal instruit și de un proces real împotriva contaminării încrucișate în bucătărie.',
      },
    ],
  },
  closingNote: {
    heading: 'O notă despre acest ghid',
    body: 'Acesta este un ghid general, menit să te ajute să te organizezi, nu o consultanță juridică. Regulile naționale prevăzute la articolul 44 diferă în UE și se actualizează, așa că verifică regulile din țara în care operezi sau întreabă autoritatea națională de siguranță alimentară.',
  },
  emailCapture: {
    heading: 'Păstrează acest ghid la îndemână',
    title: 'Primește lista de verificare a alergenilor pe email',
    description: 'Îți trimitem acest ghid, plus câte o notă practică ocazională despre meniuri și etichetarea alergenilor. Fără spam, te poți dezabona oricând.',
    buttonLabel: 'Trimite-mi-l',
  },
  cta: {
    heading: 'Pune alergenii pe un meniu în care clienții pot avea încredere',
    body: 'Marchezi cei 14 alergeni pentru fiecare preparat, actualizezi o dată, iar fiecare client vede schimbarea chiar la masă. Păstrezi o singură fișă mereu la zi, iar clienții filtrează meniul după ce este sigur pentru ei.',
    buttonLabel: 'Încearcă SafestMenu gratuit',
  },
  diagrams: {
    journey: [
      { title: 'Preparatul', sub: 'Ce conține cu adevărat, până la ultimul sos' },
      { title: 'Fișa alergenilor', sub: 'Fiecare preparat, mapat la cei 14' },
      { title: 'Meniul', sub: 'Arătat clientului, pe hârtie sau pe ecran', tag: 'SafestMenu' },
      { title: 'Clientul', sub: 'Face o alegere în care poate avea încredere' },
    ],
    routes: [{ label: 'Pe meniu' }, { label: 'Pe un afiș' }, { label: 'Comunicat la cerere' }],
    orLabel: 'sau',
    ingredients: {
      label: 'Ingrediente',
      segments: [
        { text: 'Făină de grâu', mark: true },
        { text: ', apă, ulei de rapiță, ' },
        { text: 'ou', mark: true },
        { text: ', zahăr, ' },
        { text: 'lapte', mark: true },
        { text: ', sare, malț de orz.' },
      ],
      note: 'Alergenii evidențiați în lista de ingrediente.',
    },
  },
  reference: {
    ariaLabel: 'Cei 14 alergeni UE',
    allergenLabel: 'Alergen',
    commonlyIn: 'De obicei în',
    hidesIn: 'Se ascunde în',
    remember: 'Reține',
    footnotePre: 'Atinge oricare dintre cei ',
    footnotePost: ' pentru a vedea unde se ascunde. Exemplele sunt un punct de plecare - verifică mereu propriile rețete și fișele furnizorilor.',
  },
  items: {
    'cereals-gluten': {
      name: 'Gluten',
      short: 'Grâu, secară, orz, ovăz, alac',
      common: 'Pâine, paste, aluat de pizza, prăjituri, foietaj, aluat de clătite, pesmet, cușcuș, bere și majoritatea sosurilor și supelor îngroșate cu făină.',
      hidden: 'Sos de soia (de obicei fermentat cu grâu), cuburi de supă și granule de sos, oțet de malț, unii cârnați și înghețate (cu pesmet sau umplutură de grâu), și amestecuri de condimente.',
      note: 'Ovăzul este natural fără gluten, dar aproape întotdeauna contaminat încrucișat la măcinare. Nu trata un preparat cu ovăz simplu ca fiind sigur pentru persoanele celiace, decât dacă este certificat fără gluten.',
    },
    crustaceans: {
      name: 'Crustacee',
      short: 'Creveți, crab, homar, langustine',
      common: 'Creveți, crab, homar, langustine, raci de râu și scampi.',
      hidden: 'Pastă de creveți (belacan) în curry thailandez și din Asia de Sud-Est, fond de fructe de mare în supe și risotto, baze de bouillabaisse și paella, și chipsuri de creveți.',
      note: 'O urmă de fond de creveți într-o bază de curry „cu legume” e suficientă pentru a declanșa o reacție severă. Verifică wok-urile, friteuzele și oalele comune.',
    },
    eggs: {
      name: 'Ou',
      short: 'Maioneză, prăjituri, glazuri, cremă',
      common: 'Maioneză, quiche, omletă, prăjituri, bezea, paste proaspete cu ou, cremă de vanilie și preparate în aluat sau pesmet.',
      hidden: 'Glazură de ou pe foietaj și pâine, consomeuri limpezite cu albuș, spume, marshmallow și spuma de pe cocktailuri.',
      note: 'Glazura de ou de pe produsele de patiserie e ușor de trecut cu vederea la o privire rapidă pe meniu. Marcheaz-o, nu te baza doar pe descrierea produsului.',
    },
    fish: {
      name: 'Pește',
      short: 'File, anșoa, sosuri',
      common: 'File de pește, batoane de pește, sushi, plăcintă cu pește, somon afumat și anșoa.',
      hidden: 'Sos Worcestershire și dressing Caesar (anșoa), sos de pește în preparate thailandeze și vietnameze, unele sosuri de pizza și paste, amestecuri de sos de stridii și cuburi de supă.',
      note: 'Sosul de pește și sosul Worcestershire sunt cele două surse clasice invizibile de pește care se strecoară în preparate altfel din carne sau legume.',
    },
    peanuts: {
      name: 'Alune',
      short: 'Arahide, satay, ulei de arahide',
      common: 'Unt de arahide, sos satay, arahide prăjite și sărate, ulei de arahide și multe preparate asiatice și africane.',
      hidden: 'Sosuri de chili și curry îmbogățite cu arahide măcinate, ciocolată și deserturi realizate pe linii comune de producție și proteine vegetale hidrolizate.',
      note: '„Groundnut oil” și „arachis oil” sunt doar alte denumiri pentru uleiul de arahide, și trebuie declarate amândouă. Uleiul de arahide rafinat nu este scutit - doar uleiul de soia complet rafinat beneficiază de această excepție.',
    },
    soybeans: {
      name: 'Soia',
      short: 'Sos de soia, tofu, lecitină',
      common: 'Sos de soia, tofu, edamame, miso, lapte de soia și tempeh.',
      hidden: 'Multe sosuri și marinate asiatice (hoisin, teriyaki), amestecuri de ulei vegetal, mezeluri cu adaos de soia, unele pâini și lecitină de soia (E322).',
      note: 'Lecitina de soia (E322) se regăsește într-o mare parte din ciocolată, produse tartinabile și produse de patiserie. Verifică fișele furnizorilor, nu doar ingredientul vizibil.',
    },
    milk: {
      name: 'Lapte',
      short: 'Brânză, unt, smântână, zer',
      common: 'Lapte, brânză, unt, smântână, iaurt și înghețată.',
      hidden: 'Praf de lapte în unele pâini și aluaturi, lianți din proteine din lapte în mezeluri, condimente din zer pe chipsuri, cazeinat de sodiu în toppinguri „non-lactate” și sosuri roux.',
      note: 'Proteina din lapte (cazeină sau zer) apare în produse etichetate „non-lactate” și în pâine. Nu presupune niciodată că o pâine simplă sau un condiment uscat sunt fără lactate, fără să verifici.',
    },
    'tree-nuts': {
      name: 'Nuci',
      short: 'Migdale, alune de pădure, nuci, caju',
      common: 'Migdale, alune de pădure, nuci, caju, nuci pecan, nuci braziliene, fistic și macadamia - în prăjituri, pesto, uleiuri de nuci, lapte de nuci și praline.',
      hidden: 'Pesto și dressinguri de salată, uleiuri de nuci folosite la prăjit sau la finisare, sosuri korma și alte sosuri îngroșate cu nuci măcinate, marțipan și frangipane, granola și lichioruri de nuci.',
      note: 'Latte-urile cu lapte de migdale și pastele cu pesto sunt două capcane frecvente. Ambele ascund nuci în spatele unui nume care nu spune „nucă”.',
    },
    celery: {
      name: 'Țelină',
      short: 'Rădăcină de țelină, fond, condimente',
      common: 'Bețe de țelină, rădăcină de țelină, supe, tocănițe și salate.',
      hidden: 'Cuburi de supă și fond (sarea de țelină este o bază frecventă), granule de sos, amestecuri de condimente și sare aromatizată, unii cârnați și sosuri gata preparate.',
      note: 'Sarea de țelină se ascunde în aproape orice cub de supă comercial și amestec de condimente. Verifică fondul de bază înainte să numești o supă sau un sos „fără țelină”.',
    },
    mustard: {
      name: 'Muștar',
      short: 'Condiment, dressinguri, marinate',
      common: 'Muștar ca și condiment, muștar praf, dressinguri de salată și piccalilli.',
      hidden: 'Maioneză și multe sosuri cumpărate din comerț, marinate și amestecuri BBQ, pudre de curry și amestecuri de condimente, unele pâini și mezeluri.',
      note: 'Muștarul este un ingredient ascuns în maioneză și marinate. Rareori apare în numele preparatului, dar este foarte des în sos.',
    },
    sesame: {
      name: 'Susan',
      short: 'Chifle, tahini, hummus',
      common: 'Semințe de susan pe pâine și chifle, tahini, hummus, halva, ulei de susan și falafel.',
      hidden: 'Semințe coapte pe partea de jos a chiflelor, amestecuri de condimente (za’atar, dukkah), dressinguri pe bază de tahini și friteuze comune cu produse pane cu susan.',
      note: 'Susanul este adesea copt pe partea de jos a unei chifle, nu doar presărat deasupra. O privire rapidă asupra pâinii poate să îl scape.',
    },
    sulphites: {
      name: 'Dioxid de sulf și sulfiți',
      short: 'Vin, fructe uscate, oțet',
      common: 'Vin (mai ales alb și rosé), fructe uscate precum caise și stafide, cordial, suc de fructe și cidru.',
      hidden: 'Mezeluri și cârnați, legume murate și conservate, unele produse din cartofi tăiați și congelați, sosuri pe bază de oțet și unele băuturi răcoritoare.',
      note: 'Pragul legal este o concentrație de peste 10 mg/kg sau 10 mg/l, nu simpla prezență. Caisele uscate și vinul ieftin sunt cele două surse cel mai des uitate.',
    },
    lupin: {
      name: 'Lupin',
      short: 'Făină de lupin, amestecuri „fără alergeni”',
      common: 'Făină de lupin și boabe de lupin (lupini), plus unele pâini și produse de patiserie fără gluten.',
      hidden: 'Amestecuri de făină de grâu și mixuri de copt care folosesc lupinul ca supliment de proteine, unele paste, înlocuitori vegetali de carne, și pâini și produse de patiserie de tip continental.',
      note: 'Lupinul este rar în bucătăria tradițională britanică, dar frecvent în pâinile continentale și în amestecurile de făină fără gluten. Verifică orice făină importată sau etichetată „fără alergeni”.',
    },
    molluscs: {
      name: 'Moluște',
      short: 'Midii, stridii, calamar, melci',
      common: 'Midii, scoici, stridii, scoici Sf. Iacob, calamar, caracatiță și melci (escargot).',
      hidden: 'Sos de stridii în sotéuri și marinate, fond de fructe de mare în paella și risotto, cerneală de calamar care colorează pasta sau orezul, și extensii de surimi.',
      note: 'Sosul de stridii este clasica sursă ascunsă de moluște. Apare în sotéuri și marinate care altfel par doar din carne sau legume.',
    },
  },
};
